// ============================================
// Patient Analysis Firestore Service
//
// Persists Captain (Lab Analyst) + Protocol Builder output
// so Phase 2 MCP tools can read cached analysis via Admin SDK.
//
// Collection: patient_analyses/{firebaseAuthUID}
// Single doc per user, overwritten each analysis.
// ============================================

import { doc, setDoc, getDoc, addDoc, collection, Timestamp } from 'firebase/firestore';
import { db, getAuthUid } from '../firebase';
import type { BiomarkerResult, LabReport } from '../../types';
import type { Protocol } from '../../types';
import { C_PATIENT_ANALYSES, C_ANALYSIS_ERRORS } from '../../constants/collections';

const COLLECTION = C_PATIENT_ANALYSES;

// ── Gate status computation ──

function computeGateStatus(biomarkers: BiomarkerResult[]) {
  const flagged = biomarkers.filter((b) => b.hemologixStatus !== 'optimal');
  const critical = biomarkers.filter((b) => b.hemologixStatus === 'critical');
  const hormonalSuboptimal = biomarkers.filter(
    (b) => b.category === 'hormonal' && b.hemologixStatus !== 'optimal',
  );
  return {
    flaggedCount: flagged.length,
    criticalCount: critical.length,
    gate1Active: flagged.length > 0,
    gate2Eligible: critical.length > 0 || hormonalSuboptimal.length > 0,
  };
}

// ── Write: Captain output ──

/** Persist Captain (Lab Analyst) output after analysis completes. Fire-and-forget. */
export async function persistCaptainOutput(report: LabReport): Promise<void> {
  const uid = await getAuthUid();
  const gates = computeGateStatus(report.biomarkers);

  // Slim biomarkers — strip eliteAverage and standardStatus (not needed for MCP reads)
  const biomarkers = report.biomarkers.map(
    ({ biomarker, value, unit, standardRange, functionalRange, hemologixStatus, category, interpretation }) => ({
      biomarker, value, unit, standardRange, functionalRange, hemologixStatus, category, interpretation,
    }),
  );

  await setDoc(doc(db, COLLECTION, uid), stripUndefined({
    biomarkers,
    totalBiomarkers: report.biomarkers.length,
    ...gates,
    protocol: null,
    analysisDate: new Date().toISOString(),
    protocolDate: null,
    source: report.source,
    _createdAt: Timestamp.now(),
    _updatedAt: Timestamp.now(),
  }));
}

// ── Write: Protocol output (merge into existing doc) ──

/** Persist Protocol Builder output. Merges into existing analysis doc. */
export async function persistProtocolOutput(protocol: Protocol): Promise<void> {
  const uid = await getAuthUid();

  const slimProtocol = {
    id: protocol.id,
    supplements: protocol.supplements.map(
      ({ name, dosage, timing, rationale, targetBiomarker, evidenceLevel, requiresRx }) => ({
        name, dosage, timing, rationale, targetBiomarker, evidenceLevel, requiresRx: requiresRx ?? false,
      }),
    ),
    tierRecommendation: protocol.tierRecommendation,
    bundleName: protocol.bundleName ?? null,
    bundlePrice: protocol.bundlePrice ?? null,
    warnings: protocol.warnings,
  };

  await setDoc(doc(db, COLLECTION, uid), stripUndefined({
    protocol: slimProtocol,
    protocolDate: new Date().toISOString(),
    _updatedAt: Timestamp.now(),
  }), { merge: true });
}

// ── Read: Protocol supplements for catalog matching ──

/**
 * Fetch protocol supplement names for a returning authenticated patient.
 * Used by the catalog page to highlight "In Your Protocol" products
 * when the patient navigates directly to /catalog without going
 * through the session flow.
 *
 * Returns null if no analysis or protocol exists.
 */
export async function getPatientProtocolSupplements(
  uid: string,
): Promise<{ name: string; productId?: string }[] | null> {
  const snap = await getDoc(doc(db, COLLECTION, uid));
  if (!snap.exists()) return null;
  const data = snap.data();
  if (!data.protocol?.supplements) return null;
  return data.protocol.supplements.map((s: Record<string, unknown>) => ({
    name: (s.name as string) ?? '',
    productId: s.productId as string | undefined,
  }));
}

// ── Error logging ──

/**
 * Log a Lab Analyst or Protocol Builder error to Firestore for monitoring.
 * Fire-and-forget — never blocks the error flow back to the patient.
 */
export async function logAnalysisError(
  agent: 'lab-analyst' | 'protocol-builder',
  errorMessage: string,
  context?: Record<string, unknown>,
): Promise<void> {
  try {
    const uid = await getAuthUid().catch(() => 'anonymous');
    await addDoc(collection(db, C_ANALYSIS_ERRORS), stripUndefined({
      agent,
      error: errorMessage,
      uid,
      context: context ?? null,
      timestamp: Timestamp.now(),
      date: new Date().toISOString(),
    }));
  } catch (logErr) {
    // Logging failure must never mask the original error
    console.warn('[Analysis Error Log] Failed to write to Firestore:', logErr);
  }
}

// ── BIOMARKER_NOT_FOUND logging (CAP.10 D#2) ──

/**
 * Log a BIOMARKER_NOT_FOUND event to Firestore for operational dashboard monitoring.
 * These events indicate alias map gaps — ops reviews them to add missing aliases.
 * Fire-and-forget — never blocks the extraction flow.
 */
export async function logBiomarkerNotFound(
  rawName: string,
  patientSex?: 'Male' | 'Female',
): Promise<void> {
  try {
    const uid = await getAuthUid().catch(() => 'anonymous');
    await addDoc(collection(db, C_ANALYSIS_ERRORS), stripUndefined({
      agent: 'lab-analyst',
      error: `BIOMARKER_NOT_FOUND: "${rawName}"`,
      eventType: 'BIOMARKER_NOT_FOUND',
      uid,
      context: {
        rawName,
        patientSex: patientSex ?? null,
      },
      timestamp: Timestamp.now(),
      date: new Date().toISOString(),
    }));
  } catch (logErr) {
    console.warn('[BIOMARKER_NOT_FOUND Log] Failed to write to Firestore:', logErr);
  }
}

// ── Utils ──

// eslint-disable-next-line @typescript-eslint/no-explicit-any
function stripUndefined(obj: any): any {
  if (obj === null || obj === undefined || typeof obj !== 'object') return obj;
  if (Array.isArray(obj)) return obj.map(stripUndefined);
  const cleaned: Record<string, unknown> = {};
  for (const [key, value] of Object.entries(obj)) {
    if (value !== undefined) {
      cleaned[key] = (value !== null && typeof value === 'object')
        ? stripUndefined(value)
        : value;
    }
  }
  return cleaned;
}
